import { useCallback } from 'react';
import useQuery from './useQuery';
import { callRestrictionResponseAtom } from '../states/meta-call-restrictions';
import { getCallRestrictions } from '../requests/get-meta-call-restrictions';
import { CallingRestrictionStatus, CallRestrictionsResponse } from '../typings/meta-call-restrictions';

type WabaCallDetails = CallRestrictionsResponse['results'][number];
type ViolationDirection = 'incoming' | 'outgoing' | 'both' | null;
type WabaViolationEntry = {
  direction: ViolationDirection;
  status: CallingRestrictionStatus.VIOLATED | CallingRestrictionStatus.RESTRICTED;
};
type WabaViolationDirectionMap = Record<string, WabaViolationEntry>;

const OUTGOING_CALLING_TYPES = new Set([
  'RESTRICTED_BUSINESS_INITIATED_CALLING',
  'LOW_BUSINESS_INITIATED_CALLING_QUALITY',
]);

const INCOMING_CALLING_TYPES = new Set(['RESTRICTED_USER_INITIATED_CALLING', 'LOW_USER_INITIATED_CALLING_QUALITY']);

const getDirectionFromTypes = (types: string[]): ViolationDirection => {
  const hasOutgoing = types.some((type) => OUTGOING_CALLING_TYPES.has(type));
  const hasIncoming = types.some((type) => INCOMING_CALLING_TYPES.has(type));

  if (hasIncoming && hasOutgoing) return 'both';
  if (hasIncoming) return 'incoming';
  if (hasOutgoing) return 'outgoing';
  return null;
};

export const useGetMetaCallRestrictions = () => {
  const {
    requestData: { data, loading, error },
  } = useQuery<CallRestrictionsResponse>({
    queryFunction: getCallRestrictions,
    requestAtom: callRestrictionResponseAtom,
    cacheData: true,
    autoFetch: true,
    getCachedData: true,
  });

  const getWabaDetailsByStatus = useCallback(
    (status: CallingRestrictionStatus): WabaCallDetails[] => {
      if (loading || error || !data) return [];

      return (data.results ?? []).filter((wabaDetails) => wabaDetails.callingStatus === status);
    },
    [data, loading, error]
  );

  const getRestrictedWabaDetails = useCallback((): WabaCallDetails[] => {
    return getWabaDetailsByStatus(CallingRestrictionStatus.RESTRICTED);
  }, [getWabaDetailsByStatus]);

  const getViolatedWabaDetails = useCallback((): WabaCallDetails[] => {
    return getWabaDetailsByStatus(CallingRestrictionStatus.VIOLATED);
  }, [getWabaDetailsByStatus]);

  const isIntegrationIdRestricted = useCallback(
    (integrationId: string): boolean => {
      if (loading || error || !data) return false;

      return (data.results ?? []).some(
        (wabaDetails) =>
          wabaDetails.integrationId === integrationId &&
          wabaDetails.callingStatus === CallingRestrictionStatus.RESTRICTED
      );
    },
    [data, loading, error]
  );

  const getWabaViolationDirectionMap = useCallback((): WabaViolationDirectionMap => {
    if (loading || error || !data) return {};

    return (data.results ?? []).reduce<WabaViolationDirectionMap>((acc, wabaDetails) => {
      const wabaId = wabaDetails.integrationId;
      if (
        wabaDetails.callingStatus !== CallingRestrictionStatus.VIOLATED &&
        wabaDetails.callingStatus !== CallingRestrictionStatus.RESTRICTED
      ) {
        return acc;
      }

      const types =
        wabaDetails.callingStatus === CallingRestrictionStatus.RESTRICTED
          ? (wabaDetails.callingRestrictions ?? []).map((restriction) => restriction.restrictionType)
          : (wabaDetails.callingViolations ?? []).map((violation) => violation.violationType);
      if (!types.length) return acc;
      acc[wabaId] = {
        direction: getDirectionFromTypes(types),
        status: wabaDetails.callingStatus,
      };
      return acc;
    }, {});
  }, [data, loading, error]);

  return {
    getRestrictedWabaDetails,
    getViolatedWabaDetails,
    getWabaViolationDirectionMap,
    isIntegrationIdRestricted,
    loading,
    error,
    data,
  };
};

export default useGetMetaCallRestrictions;
